import type { BunkerGrade } from '../components/pnl/co2/co2Cost';

export interface ResolvedBunkerGrade {
  id: number;
  gradeName: string;
  carbonFactor: number | null;
  ch4Factor: number | null;
  lcv: number | null;
  n2oFactor: number | null;
  wttFactor: number | null;
  /** Precomputed fuel cost per tonne in default currency */
  fuelCost: number;
}

export interface BlendGradeNode {
  id: number;
  isBlend: boolean;
  blendComponent1Id?: number | null;
  blendComponent2Id?: number | null;
  blendPercentage1?: string | number | null;
  blendPercentage2?: string | number | null;
  carbonFactor?: string | number | null;
  ch4Factor?: string | number | null;
  lcv?: string | number | null;
  n2OFactor?: string | number | null;
  wttFactor?: string | number | null;
}

function toNum(value: string | number | null | undefined): number | null {
  if (value == null) return null;
  const n = Number(value);
  return Number.isNaN(n) ? null : n;
}

export function blendFactor(
  v1: number | null,
  v2: number | null,
  pct1: number,
  pct2: number,
): number | null {
  if (v1 === null || v2 === null) return null;
  return v1 * pct1 + v2 * pct2;
}

/** Resolves a BunkerGrade's emission factors, computing weighted averages for blends. */
export function resolveBunkerGradeFactors<T extends BlendGradeNode>(
  grade: T,
  allGrades: T[],
): BunkerGrade {
  if (!grade.isBlend) {
    return {
      id: grade.id,
      carbonFactor: toNum(grade.carbonFactor),
      ch4Factor: toNum(grade.ch4Factor),
      lcv: toNum(grade.lcv),
      n2oFactor: toNum(grade.n2OFactor),
      wttFactor: toNum(grade.wttFactor),
    };
  }

  const comp1 = allGrades.find((g) => g.id === grade.blendComponent1Id);
  const comp2 = allGrades.find((g) => g.id === grade.blendComponent2Id);

  if (!comp1 || !comp2) {
    return {
      id: grade.id,
      carbonFactor: null,
      ch4Factor: null,
      lcv: null,
      n2oFactor: null,
      wttFactor: null,
    };
  }

  const pct1 = Number(grade.blendPercentage1) / 100;
  const pct2 = Number(grade.blendPercentage2) / 100;

  return {
    id: grade.id,
    carbonFactor: blendFactor(
      toNum(comp1.carbonFactor),
      toNum(comp2.carbonFactor),
      pct1,
      pct2,
    ),
    ch4Factor: blendFactor(
      toNum(comp1.ch4Factor),
      toNum(comp2.ch4Factor),
      pct1,
      pct2,
    ),
    lcv: blendFactor(toNum(comp1.lcv), toNum(comp2.lcv), pct1, pct2),
    n2oFactor: blendFactor(
      toNum(comp1.n2OFactor),
      toNum(comp2.n2OFactor),
      pct1,
      pct2,
    ),
    wttFactor: blendFactor(
      toNum(comp1.wttFactor),
      toNum(comp2.wttFactor),
      pct1,
      pct2,
    ),
  };
}

interface PricingGradeNode extends BlendGradeNode {
  gradeName: string;
}

/**
 * Builds a map of grade ID → ResolvedBunkerGrade with precomputed emission factors and fuel cost.
 * Price resolution is delegated to the provided resolvePriceFn which should handle blends,
 * BunkerEx lookups, price adjustments, and currency conversion.
 */
export function resolveAllBunkerGrades<T extends PricingGradeNode>(
  grades: T[],
  resolvePriceFn: (gradeName: string) => number | null,
): Map<number, ResolvedBunkerGrade> {
  const map = new Map<number, ResolvedBunkerGrade>();

  for (const grade of grades) {
    const factors = resolveBunkerGradeFactors(grade, grades);
    map.set(grade.id, {
      ...factors,
      gradeName: grade.gradeName,
      fuelCost: resolvePriceFn(grade.gradeName) ?? 0,
    });
  }

  return map;
}
